// Sound and vibration when a notification arrives while the app is open.
// Push notifications (app closed) use the device's own sound and vibration
// (see public/push-sw.js). Both settings are per device and on by default.

const SOUND_KEY = "somigo.notificationSound";
const VIBRATION_KEY = "somigo.notificationVibration";
const SOUND_URL = "/notification-sound.wav";
const VIBRATION_PATTERN = [200, 100, 200];

let audio = null;

const readSetting = (key) => {
  try {
    return localStorage.getItem(key) !== "off";
  } catch {
    return true;
  }
};

const writeSetting = (key, enabled) => {
  try {
    localStorage.setItem(key, enabled ? "on" : "off");
  } catch {
    // Storage can be blocked (e.g. private mode) - the setting just isn't kept
  }
};

export const isSoundEnabled = () => readSetting(SOUND_KEY);
export const setSoundEnabled = (enabled) => writeSetting(SOUND_KEY, enabled);
export const isVibrationEnabled = () => readSetting(VIBRATION_KEY);
export const setVibrationEnabled = (enabled) =>
  writeSetting(VIBRATION_KEY, enabled);

// Vibration only works in Android browsers (not on iPhone or desktop)
export const isVibrationSupported = () =>
  typeof navigator !== "undefined" && "vibrate" in navigator;

const getAudio = () => {
  if (!audio) audio = new Audio(SOUND_URL);
  return audio;
};

export const playNotificationSound = () => {
  const sound = getAudio();
  sound.muted = false;
  sound.currentTime = 0;
  // Browsers block sound until the user has tapped or clicked in the app
  sound.play().catch(() => {});
};

// Browsers only let a page play sound after the user has clicked, tapped or
// typed in it - and Safari only for a sound that was started during such an
// interaction. So the sound is played silently once on the first one, which
// lets it play later when a notification or chat message arrives.
export const installSoundUnlock = () => {
  const events = ["pointerdown", "keydown", "touchstart"];
  const unlock = () => {
    events.forEach((event) => window.removeEventListener(event, unlock, true));
    const sound = getAudio();
    sound.muted = true;
    sound
      .play()
      .then(() => {
        sound.pause();
        sound.currentTime = 0;
        sound.muted = false;
      })
      .catch(() => {
        sound.muted = false;
      });
  };
  events.forEach((event) => window.addEventListener(event, unlock, true));
};

// Only alert in a visible window: a hidden app gets the push notification
// instead, and only the tab that is being looked at should make a sound.
// Chat messages have no push, so they alert in a hidden window too
// (evenIfHidden).
export const alertNewNotification = ({ evenIfHidden = false } = {}) => {
  if (!evenIfHidden && document.visibilityState !== "visible") return;
  if (isSoundEnabled()) playNotificationSound();
  if (isVibrationEnabled() && isVibrationSupported()) {
    navigator.vibrate(VIBRATION_PATTERN);
  }
};
